import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import RefreshOverlay from 'Components/DesignSystem/Table/RefreshOverlay';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Button from 'Components/DesignSystem/Button/Button';
import { ArrowDown, ArrowRight, Sort, Hashtag } from 'Components/DesignSystem/Iconography/Icons';

// Local Components
import { ActionBar, EditingBanner, RowActionsMenu, ProductNotFoundIndicator, LoadingOverlay, ExpandedRowContent, TablePaginationBar } from '../../components';
import SearchReplaceSidePanel from '../../components/SearchReplaceSidePanel';
import { IgnoreProductModal, ConfirmActionModal, CreateAttributeModal, CreateAttributeValueModal } from '../../modals';

// API Services
import {
  getDataReviewProducts,
  saveBulkEditChanges,
  BulkActionType,
  replaceProduct,
  getDataReviewAttributeValues,
  getDataReviewAttributes,
} from 'api/DataReview';

// Utilities
import { transformToSKUMatchedRow, transformPagination, PaginationInfo, getEffectiveValues, getMergedValues } from '../../utils';
import { useBulkAction } from '../../hooks';

// Types
import { SKUMatchedRow, RowActionType, ProductMetadata } from '../../types';
import type { IgnoreProductModalData } from '../../types';

// Constants
import { UNMATCHED_ACTIONS, UNMATCHED_BULK_ROW_ACTIONS } from '../../constants';

// Small inline search icon — matches the one used on the MPN/UPC Matched tab.
// Kept local so we don't add a new design-system icon export for one button.
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <circle cx="7" cy="7" r="5" strokeWidth="1.5" />
    <path strokeLinecap="round" strokeWidth="1.5" d="M11 11l3 3" />
  </svg>
);

// View props interface
interface UnmatchedViewProps {
  channelKey: string;
  onRefresh?: () => void;
  onEditModeChange?: (editing: boolean) => void;
  onRegisterEditHandlers?: (handlers: { save: () => Promise<boolean>; cancel: () => void } | null) => void;
  hasUpdatePermission?: boolean;
  // gmscatalogue_bulkupdate — gates the "Create All" whole-tab button
  // (ticket 13765). Hidden, not disabled, when absent.
  hasBulkUpdatePermission?: boolean;
}

// Default pagination values
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

// Sort configuration
type SortField = 'updated_at' | 'name' | 'category' | 'total_new_count' | 'total_updates_count';
type SortOrder = 'asc' | 'desc';

const DEFAULT_SORT_BY: SortField = 'updated_at';
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

/**
 * UnmatchedView Component
 *
 * Displays imported products with no catalog match using SINGLE UNIFIED TABLE
 * - Product Catalog (left): Shows "Product Not Found" indicator with Search button
 * - Imported Products (right): Shows imported product details
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11674-17723
 */
const UnmatchedView: React.FC<UnmatchedViewProps> = ({ channelKey, onRefresh, onEditModeChange, onRegisterEditHandlers, hasUpdatePermission = true, hasBulkUpdatePermission = false }) => {
  // State
  const [searchValue, setSearchValue] = useState('');
  // Debounced mirror of searchValue. The data fetch keys off this (not the raw
  // value) so typing fires ONE request after 300ms instead of one per keystroke
  // (search=c, search=co, search=col, ...). The input stays bound to searchValue
  // for instant feedback. Mirrors the debounce already used in MissingMappingView.
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchValue(searchValue.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchValue]);
  const [rows, setRows] = useState<SKUMatchedRow[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 0,
  });
  const [sidePanelRowIndex, setSidePanelRowIndex] = useState<number | null>(null);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  // Tracks the SKUs being ignored — used by both the per-row Ignore
  // (single element) and the bulk Ignore (multiple elements) paths.
  // Previously single-only, so the bulk Ignore modal's submit silently
  // bailed at the `if (!pendingIgnoreSku) return` guard.
  const [pendingIgnoreSkus, setPendingIgnoreSkus] = useState<string[]>([]);
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  // Loading flag for the Search & Add flow lives on the side panel —
  // see MPNUPCMatchedView for the rationale (don't flash a page-level
  // overlay over a panel that's about to close).
  const [isReplacingProduct, setIsReplacingProduct] = useState(false);

  // "Delete attribute?" Save validation popup (ticket 11443).
  const [deleteAttrConfirm, setDeleteAttrConfirm] = useState<{
    isOpen: boolean;
    names: string[];
    onConfirm: () => void;
  }>({ isOpen: false, names: [], onConfirm: () => {} });

  // Confirmation modal state (optional title used by the whole-tab
  // "Create All" confirmation, ticket 13765)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean; title?: string; message: string; confirmLabel: string; onConfirm: () => void;
  }>({ isOpen: false, message: '', confirmLabel: 'Confirm', onConfirm: () => {} });
  const showConfirm = (message: string, onConfirm: () => void, confirmLabel = 'Confirm') => {
    setConfirmModal({ isOpen: true, message, confirmLabel, onConfirm });
  };
  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    // Clear selection on confirm-done AND cancel.
    clearSelection();
  };

  // Edit-mode inline-create modals: opened from the dropdowns' "Create" link.
  // `rowIndex` + `attributeName` / `attrIndex` carry the context needed to
  // splice the created entity back into the row on success.
  const [createValueModal, setCreateValueModal] = useState<{
    rowIndex: number;
    attributeName: string;
  } | null>(null);
  const [createAttributeModal, setCreateAttributeModal] = useState<{
    rowIndex: number;
    attrIndex: number;
  } | null>(null);

  // Computed values
  const selectedCount = rows.filter((r) => r.isSelected).length;

  // Tracks whether the component is still mounted. Guards setState calls
  // inside async fetchProducts so a fetch that resolves after tab-switch
  // doesn't fire a "state update on unmounted component" warning.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch products from API
   */
  const fetchProducts = useCallback(async () => {
    if (!channelKey) return;

    setIsLoading(true);
    try {
      const response = await getDataReviewProducts(channelKey, {
        tab: 'unmatched',
        page: pagination.page,
        per_page: pagination.perPage,
        search: debouncedSearchValue || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (!isMountedRef.current) return;

      if (response.done && response.data) {
        // Transform API data to frontend format
        const transformedRows = response.data.map((product) =>
          transformToSKUMatchedRow(product)
        );
        // Preserve expanded state from previous rows
        setRows((prevRows) => {
          const expandedIds = new Set(prevRows.filter((r) => r.isExpanded).map((r) => r.id));
          return transformedRows.map((row) => ({
            ...row,
            isExpanded: expandedIds.has(row.id),
          }));
        });
        setPagination(transformPagination(response.page));
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch unmatched products:', error);
      toast.error('Failed to load unmatched products');
      setRows([]);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [channelKey, pagination.page, pagination.perPage, debouncedSearchValue, sortBy, sortOrder]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Refresh after an action that moves the row out of Unmatched (update,
  // create_new, ignore) — clear the search and reset pagination so the user
  // sees the full list, since a stale search filter would hide other rows.
  // NOT used after edit save/cancel or Search & Add, since those keep the
  // row in Unmatched and the user likely wants their filter preserved.
  //
  // See SKUMatchedView.refreshAfterAction for why we avoid calling
  // fetchProducts() directly when state actually changes — the stale
  // closure races with the useEffect-driven refetch.
  // Clear select-all + per-row selection state.
  const clearSelection = () => {
    setSelectAll(false);
    setRows((prev) => prev.map((r) => (r.isSelected ? { ...r, isSelected: false } : r)));
  };

  const refreshAfterAction = () => {
    const searchWasSet = searchValue !== '';
    const pageWasAtDefault = pagination.page === DEFAULT_PAGE;
    setSearchValue('');
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    if (!searchWasSet && pageWasAtDefault) {
      fetchProducts();
    }
    clearSelection();
    onRefresh?.();
  };

  // Hooks
  const { isBulkActionLoading, hasPendingTasks, executeBulkAction, executeBulkAllAction } = useBulkAction({ channelKey, sourceTab: 'unmatched', onTaskComplete: () => { refreshAfterAction(); } });

  // "Create All" (ticket 13765): whole-tab bulk create — every Unmatched
  // row across all pages becomes a brand-new product (new GMS ID); matching
  // is skipped entirely, including rows with a partial SME mapping in
  // progress. Confirmation copy and success toast are verbatim from the AC.
  const handleCreateAll = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Create all unmatched products as new?',
      message: 'This will create these as new products without matching them to existing ones.',
      confirmLabel: 'Create All',
      onConfirm: () => {
        closeConfirm();
        executeBulkAllAction('create_new', {
          successMessage: () => 'All Unmatched products created and moved to Processed.',
        });
      },
    });
  };

  // Handlers
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleBulkAction = async (action: string) => {
    const selectedRows = rows.filter((r) => r.isSelected);
    const allSelectedSkus = selectedRows.map((r) => r.imported.sku).filter(Boolean) as string[];

    if (allSelectedSkus.length === 0) {
      toast.error('No products selected');
      return;
    }

    if (action === 'ignore') {
      setPendingIgnoreSkus(allSelectedSkus);
      setShowIgnoreModal(true);
      return;
    }

    // Update partitions selected rows by whether a catalog product has been
    // picked via Search & Add. Rows without a pending match are skipped because
    // the backend's action="update" is a no-op when matched_gms_id is empty.
    if (action === 'update') {
      const updatableSkus = selectedRows
        .filter((r) => r.matchedGmsId)
        .map((r) => r.imported.sku)
        .filter(Boolean) as string[];
      const skippedCount = selectedRows.length - updatableSkus.length;

      if (updatableSkus.length === 0) {
        toast.error('None of the selected products have a matched catalog product. Use Search & Add first.');
        return;
      }

      const message = skippedCount > 0
        ? `${updatableSkus.length} of ${selectedRows.length} selected products can be updated. ${skippedCount} product${skippedCount === 1 ? '' : 's'} do not have a matched product and will be skipped. Do you want to proceed?`
        : `Are you sure you want to update ${updatableSkus.length} selected product(s)? All changes will be applied.`;

      showConfirm(message, () => {
        closeConfirm();
        executeBulkAction(updatableSkus, 'update', undefined, {
          successMessage: (r) => `${r.success_count} product${r.success_count === 1 ? '' : 's'} updated successfully.`,
          suppressFailureToast: true,
        }).then((success) => {
          if (success) { refreshAfterAction(); }
        });
      });
      return;
    }

    // Create New works for all selected rows regardless of catalog selection.
    if (action === 'create_new') {
      showConfirm(
        `Create ${allSelectedSkus.length} new product${allSelectedSkus.length === 1 ? '' : 's'}?`,
        () => {
          closeConfirm();
          executeBulkAction(allSelectedSkus, 'create_new', undefined, {
            successMessage: (r) => `${r.success_count} product${r.success_count === 1 ? '' : 's'} created successfully.`,
          }).then((success) => {
            if (success) { refreshAfterAction(); }
          });
        },
        'Create',
      );
      return;
    }

    // Fallback for any other action types — preserve prior behavior.
    showConfirm(
      `Are you sure you want to ${action} ${allSelectedSkus.length} selected product(s)?`,
      () => {
        closeConfirm();
        executeBulkAction(allSelectedSkus, action as BulkActionType).then((success) => {
          if (success) { refreshAfterAction(); }
        });
      },
    );
  };

  const handleFilterClick = () => {
    // TODO: Implement filter functionality
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sortBy === field) {
      return (
        <span className={`tw-text-accent ${sortOrder === 'asc' ? 'tw-rotate-180' : ''} tw-inline-block tw-transition-transform`}>
          <ArrowDown />
        </span>
      );
    }
    return <span className="tw-text-ink-tertiary"><Sort /></span>;
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setRows((prev) => prev.map((row) => ({ ...row, isSelected: checked })));
  };

  const handleRowSelect = (index: number, selected: boolean) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isSelected: selected };
      setSelectAll(updated.every((r) => r.isSelected));
      return updated;
    });
  };

  const handleRowExpand = (index: number) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isExpanded: !updated[index].isExpanded };
      return updated;
    });
  };

  const handleRowAction = async (index: number, actionType: RowActionType) => {
    const row = rows[index];
    const sku = row?.imported?.sku;
    if (!sku) return;

    if (actionType === 'update') {
      // Defensive guard — the three-dot menu item is disabled when there's no
      // pending match, but this also protects against keyboard shortcuts or
      // programmatic triggers. The backend action="update" is a no-op without
      // match_result.matched_gms_id, so skipping the call avoids silently
      // marking the row "processed" with nothing applied.
      if (!row.matchedGmsId) return;

      showConfirm(
        'Are you sure you want to update this product? All changes will be applied.',
        () => {
          closeConfirm();
          executeBulkAction([sku], 'update', undefined, {
            successMessage: (r) => `${r.success_count} product${r.success_count === 1 ? '' : 's'} updated successfully.`,
            suppressFailureToast: true,
          }).then((success) => {
            if (success) { refreshAfterAction(); }
          });
        },
      );
      return;
    }

    if (actionType === 'create-new') {
      showConfirm(
        'Create 1 new product?',
        () => {
          closeConfirm();
          executeBulkAction([sku], 'create_new', undefined, {
            successMessage: (r) => `${r.success_count} product${r.success_count === 1 ? '' : 's'} created successfully.`,
          }).then((success) => {
            if (success) { refreshAfterAction(); }
          });
        },
        'Create',
      );
      return;
    }

    if (actionType === 'ignore') {
      setPendingIgnoreSkus([sku]);
      setShowIgnoreModal(true);
    }
  };

  const handleIgnoreProductSubmit = async (data: IgnoreProductModalData) => {
    if (pendingIgnoreSkus.length === 0) return;

    const success = await executeBulkAction(pendingIgnoreSkus, 'ignore', { reason: data.reason });
    if (success) {
      setShowIgnoreModal(false);
      setPendingIgnoreSkus([]);
      refreshAfterAction();
    }
  };

  const handleSearchProduct = (index: number) => {
    setSidePanelRowIndex(index);
  };

  // Edit mode handlers
  const handleEditToggle = () => {
    const next = !isEditMode;
    setIsEditMode(next);
    onEditModeChange?.(next);
    if (next) {
      // Clear any row/select-all selections when entering edit mode — they
      // apply to bulk actions, which are hidden in edit mode.
      setSelectAll(false);
      setRows((prev) => prev.map((r) => ({ ...r, isSelected: false })));
      onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
    } else {
      onRegisterEditHandlers?.(null);
    }
  };

  // Promise resolver held while the "Delete attribute?" popup is open so we
  // can finish the deferred Save when the user confirms or cancels.
  const pendingSaveResolveRef = useRef<((ok: boolean) => void) | null>(null);

  // Build the API payload and commit to backend — split out of handleSaveChanges
  // so the deferred path (after the delete-attribute confirm popup) can call
  // the same persistence flow without re-validating.
  const performSave = async (): Promise<boolean> => {
    // Validate PLS is not blank for edited rows
    const invalidPlsRows = rows.filter(
      (row) => editedRows.has(String(row.id)) && (!row.imported.pls || row.imported.pls.trim() === '')
    );
    if (invalidPlsRows.length > 0) {
      toast.error(`PLS is required for: ${invalidPlsRows.map((r) => r.imported.product || r.imported.sku).join(', ')}`);
      return false;
    }

    const edits = rows
      .filter((row) => editedRows.has(String(row.id)))
      .map((row) => ({
        sku: row.imported.sku || '',
        source_data: {
          prod: {
            name: row.imported.product,
            description: row.imported.description,
            price: row.imported.price,
            upc: row.imported.upc,
            model_number: row.imported.modelNumber,
            product_lifecycle_status: row.imported.pls,
            version: row.imported.version,
          },
          parent_id: row.imported.parentId,
          category: row.imported.category,
          real_sku: row.imported.realSku || '',
          category_id: row.imported.categoryId || '',
          attributes: row.attributes
            ?.filter((attr) => {
              // Existing implicit-delete: edited values cleared → drop.
              if (attr.editedValues !== undefined && attr.editedValues.length === 0) return false;
              // Pending-new placeholder with unset/temp name → drop (the
              // delete-attribute confirm popup has already informed the user).
              if (attr.isPendingNew && (!attr.name?.trim() || attr.name.startsWith('__pending_'))) return false;
              return true;
            })
            .map((attr) => {
              const finalValues = attr.editedValues ?? getEffectiveValues(attr);
              return { name: attr.name, values: finalValues };
            }) || [],
        },
      }))
      .filter((edit) => edit.sku);

    if (edits.length === 0) {
      toast.info('No changes to save');
      setIsEditMode(false);
      onEditModeChange?.(false);
      onRegisterEditHandlers?.(null);
      return true;
    }

    setIsSaveLoading(true);
    try {
      const response = await saveBulkEditChanges(channelKey, { edits });

      if (response.done) {
        toast.success(`Successfully saved ${response.success_count} edit(s)`);
        if (response.failed_count > 0) {
          toast.warning(`Failed to save ${response.failed_count} edit(s)`);
        }
        setIsEditMode(false);
        onEditModeChange?.(false);
        onRegisterEditHandlers?.(null);
        setEditedRows(new Set());
        // Edited rows sort to the top (updated_at desc) so page 1 is where
        // the user will see their changes. Reset to page 1 if we're not
        // already there; state change drives the refetch via useEffect.
        const pageWasAtDefault = pagination.page === DEFAULT_PAGE;
        if (!pageWasAtDefault) {
          setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
        } else {
          fetchProducts();
        }
        if (onRefresh) onRefresh();
        return true;
      } else {
        toast.error(response.message || 'Failed to save changes');
        return false;
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
      return false;
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    // 11443: scan edited rows for incomplete pending-new attributes. A pending
    // row counts as incomplete when its name is still the temp placeholder OR
    // its editedValues array is empty. The consolidated confirm popup tells
    // the user which attributes will be dropped (per AC wording).
    const incompletePendingLabels: string[] = [];
    rows.forEach((row) => {
      if (!editedRows.has(String(row.id))) return;
      (row.attributes || []).forEach((attr) => {
        if (!attr.isPendingNew) return;
        const hasName = !!attr.name?.trim() && !attr.name.startsWith('__pending_');
        const hasValues = (attr.editedValues?.length ?? 0) > 0;
        if (!hasName || !hasValues) {
          incompletePendingLabels.push(hasName ? attr.name : '(new attribute)');
        }
      });
    });

    if (incompletePendingLabels.length === 0) {
      return performSave();
    }

    return new Promise<boolean>((resolve) => {
      pendingSaveResolveRef.current = resolve;
      setDeleteAttrConfirm({
        isOpen: true,
        names: incompletePendingLabels,
        onConfirm: async () => {
          setDeleteAttrConfirm((p) => ({ ...p, isOpen: false }));
          const ok = await performSave();
          if (pendingSaveResolveRef.current) {
            pendingSaveResolveRef.current(ok);
            pendingSaveResolveRef.current = null;
          }
        },
      });
    });
  };

  const closeDeleteAttrConfirm = () => {
    setDeleteAttrConfirm((p) => ({ ...p, isOpen: false }));
    if (pendingSaveResolveRef.current) {
      pendingSaveResolveRef.current(false);
      pendingSaveResolveRef.current = null;
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    onEditModeChange?.(false);
    onRegisterEditHandlers?.(null);
    setEditedRows(new Set());
    setSelectAll(false);
    fetchProducts();
  };

  // Refs to keep handlers current for AppContext registration
  const saveRef = useRef(handleSaveChanges);
  saveRef.current = handleSaveChanges;
  const cancelRef = useRef(handleCancelEdit);
  cancelRef.current = handleCancelEdit;

  const handleProductNameChange = (rowIndex: number, value: string) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        imported: { ...updated[rowIndex].imported, product: value },
      };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleMetadataChange = (
    rowIndex: number,
    field: keyof ProductMetadata,
    value: string
  ) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        imported: {
          ...updated[rowIndex].imported,
          [field]: value,
        },
      };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleDeleteAttribute = (rowIndex: number, attrIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      if (updated[rowIndex].attributes) {
        const attributes = [...updated[rowIndex].attributes!];
        attributes[attrIndex] = { ...attributes[attrIndex], editedValues: [] };
        updated[rowIndex] = { ...updated[rowIndex], attributes };
        setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      }
      return updated;
    });
  };

  const handleDeleteAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attr = updated[rowIndex].attributes?.[attrIndex];
      if (!attr) return prev;
      const current = attr.editedValues ?? getEffectiveValues(attr);
      const valToRemove = getMergedValues(attr)[valueIndex];
      const newEdited = current.filter((v) => v !== valToRemove);
      updated[rowIndex] = {
        ...updated[rowIndex],
        attributes: updated[rowIndex].attributes!.map((a, i) =>
          i === attrIndex ? { ...a, editedValues: newEdited } : a
        ),
      };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleRevertAttribute = (rowIndex: number, attrIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { editedValues: _, ...rest } = attrs[attrIndex];
      attrs[attrIndex] = rest;
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      return updated;
    });
  };

  const handleRevertAttributeValue = (rowIndex: number, attrIndex: number, valueIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      const valToRestore = getMergedValues(attr)[valueIndex];
      const current = attr.editedValues ?? [];
      if (!current.includes(valToRestore)) {
        attrs[attrIndex] = { ...attr, editedValues: [...current, valToRestore] };
        updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      }
      return updated;
    });
  };

  // Append a value to an existing attribute's editedValues (inline + dropdown).
  // The dropdown is multi-select with apply-per-check, so a click adds one
  // value at a time.
  const handleAddAttributeValue = (rowIndex: number, attrIndex: number, valueToAdd: string) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attr = attrs[attrIndex];
      if (!attr) return prev;
      const current = attr.editedValues ?? getEffectiveValues(attr);
      if (current.includes(valueToAdd)) return prev;
      attrs[attrIndex] = { ...attr, editedValues: [...current, valueToAdd] };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // Add a brand-new placeholder attribute row (Add Attribute button). The
  // temporary name keeps getMatchedRows' Set dedupe from collapsing multiple
  // placeholders into one row before the user picks a real name.
  const handleAddNewAttribute = (rowIndex: number) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const tempName = `__pending_${Date.now()}_${Math.random().toString(36).slice(2, 7)}__`;
      attrs.push({
        name: tempName,
        value: '',
        values: [],
        isPendingNew: true,
        editedValues: [],
      });
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // User picked a name for the pending-new row — swap the temp name for the
  // real one so the value loader can resolve the attribute id.
  const handleSelectNewAttributeName = (rowIndex: number, attrIndex: number, name: string) => {
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      if (!attrs[attrIndex]) return prev;
      attrs[attrIndex] = { ...attrs[attrIndex], name };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  // Per-row attribute loader. Industry-scoped via the new data-review
  // endpoint (`GET /catalogue/data-review/<cs_key>/attributes`) — sibling of
  // the attribute-values endpoint. Reads Neo4j catalog so newly-created
  // attributes appear immediately. Industry scope (not category) means
  // rows whose imported product has no `categoryId` still get a usable
  // list, and attributes on the industry not linked to this row's
  // category aren't silently hidden. The dropdown still hides attributes
  // already present on the row so the user can't pick a duplicate.
  const loadAttributeOptionsForRow = useCallback(
    (rowIndex: number) => async (search: string, page: number) => {
      if (!channelKey) return { options: [], hasMore: false };
      const PAGE_SIZE = 20;
      try {
        const res = await getDataReviewAttributes(channelKey, page, PAGE_SIZE, search);
        if (!res?.done || !res.data) return { options: [], hasMore: false };
        const existing = new Set(
          (rows[rowIndex]?.attributes || [])
            .map((a) => a.name?.toLowerCase())
            .filter((n): n is string => !!n && !n.startsWith('__pending_'))
        );
        return {
          options: (res.data.items || [])
            .map((it) => ({ value: it.name, label: it.name }))
            .filter((o) => !existing.has(o.value.toLowerCase())),
          hasMore: !!res.data.pagination?.has_next,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [channelKey, rows]
  );

  // Per-row, per-attribute value loader. Reads Neo4j catalog AVs scoped
  // to the channel's industry via the data-review endpoint:
  // `GET /catalogue/data-review/:cs_key/attribute-values?attribute=:name&...`.
  //
  // This is the source of truth for "values that exist for this attribute
  // in the catalog" — including ones the user just created via the
  // `Create New Value` flow (`POST /catalogue/data-review/:cs_key/create-
  // attribute-values`), since both reads/writes hit the same Neo4j subgraph.
  //
  // Why not the Data Mapping AV layer (`GET /datamapping/<cs_key>/mapped-
  // attributes/<attr>/values`): that returns incoming→mapped pairs seen
  // in this channel's feeds. A value the user creates ad-hoc only lands
  // in Neo4j; no row ever materializes in `attributeValue_mapping`, so
  // the picker silently dropped it (12035).
  //
  // The attribute name is sent as a query param so names containing
  // slashes, spaces, or other URL-fragile characters round-trip cleanly.
  // `rowIndex` is no longer needed for the lookup (the data-review
  // endpoint scopes by cs_key + industry, not by row category), but the
  // curry is preserved for call-site compatibility.
  const loadValueOptionsForRow = useCallback(
    (_rowIndex: number) => (attributeName: string) => async (search: string, page: number) => {
      if (!attributeName || attributeName.startsWith('__pending_')) {
        return { options: [], hasMore: false };
      }
      if (!channelKey) return { options: [], hasMore: false };
      const PAGE_SIZE = 20;
      try {
        const res = await getDataReviewAttributeValues(
          channelKey,
          attributeName,
          page,
          PAGE_SIZE,
          search,
        );
        if (!res?.done || !res.data) return { options: [], hasMore: false };
        const items = res.data.items || [];
        return {
          options: items
            .map((it) => (it?.value ?? '').toString())
            .filter((v) => v.length > 0)
            .map((v) => ({ value: v, label: v })),
          hasMore: !!res.data.pagination?.has_next,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [channelKey]
  );

  const handleModalAttrValuesSave = (
    rowIndex: number,
    attrName: string,
    valuesToKeep: string[]
  ) => {
    if (!isEditMode) {
      setIsEditMode(true);
      onEditModeChange?.(true);
      onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
    }
    setRows((prev) => {
      const updated = [...prev];
      const attrs = [...(updated[rowIndex].attributes || [])];
      const attrIndex = attrs.findIndex((a) => a.name === attrName);
      if (attrIndex === -1) return prev;

      attrs[attrIndex] = { ...attrs[attrIndex], editedValues: valuesToKeep };
      updated[rowIndex] = { ...updated[rowIndex], attributes: attrs };
      setEditedRows((prevEdited) => new Set(prevEdited).add(String(updated[rowIndex].id)));
      return updated;
    });
  };

  const handleSidePanelClose = () => {
    setSidePanelRowIndex(null);
  };

  const handleReplaceProduct = async (productId: string) => {
    if (sidePanelRowIndex === null || !channelKey) return;

    const row = rows[sidePanelRowIndex];
    const sku = row.imported.sku;

    if (!sku) {
      toast.error('Cannot add product: SKU is missing');
      return;
    }

    setIsReplacingProduct(true);
    try {
      const response = await replaceProduct(channelKey, sku, {
        new_gms_id: productId,
        reason: 'Manual match via Search & Add',
      });

      if (response.done) {
        toast.success('Product added successfully');
        setSidePanelRowIndex(null);
        fetchProducts();
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Failed to add product:', error);
      toast.error('Failed to add product');
    } finally {
      setIsReplacingProduct(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    // Selection is per-page; resetting on page change so the header
    // checkbox doesn't carry stale state into a fresh row set (the new
    // page's rows come back with isSelected:false but `selectAll` would
    // otherwise stay true and the header would still show checked).
    clearSelection();
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPerPage: string | number) => {
    clearSelection();
    setPagination((prev) => ({ ...prev, perPage: newPerPage as number, page: DEFAULT_PAGE }));
  };

  return (
    <div className="tw-space-y-4 tw-relative">
      {/* Action Bar */}
      <ActionBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        /* § 288 — the table fetch, plus the 300ms debounce in front of it. `.trim()`
           on the left because that is what the debounce commits: comparing the raw
           value would leave the spinner running forever on a trailing space. */
        isSearching={isLoading || searchValue.trim() !== debouncedSearchValue}
        bulkActions={UNMATCHED_BULK_ROW_ACTIONS}
        onBulkAction={handleBulkAction}
        onFilterClick={handleFilterClick}
        isEditMode={isEditMode}
        onEditToggle={handleEditToggle}
        selectedCount={selectedCount}
        isLoading={isBulkActionLoading || isSaveLoading}
        hasUpdatePermission={hasUpdatePermission}
        showBulkAllButton={hasBulkUpdatePermission}
        bulkAllLabel="Create All"
        onBulkAllClick={handleCreateAll}
        isBulkAllDisabled={hasPendingTasks || isEditMode || pagination.total === 0}
      />

      {/* Editing Banner */}
      {isEditMode && (
        <EditingBanner onCancel={handleCancelEdit} onSave={handleSaveChanges} />
      )}

      {/* Single Unified Table — scrollable body, fixed header */}
      {(
        <div className="tw-relative tw-border tw-border-edge tw-rounded tw-flex tw-flex-col" style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '400px' }}>
          {/*
            The "Refreshing" chip Guide/ISR shows on the same gesture. These
            tables are hand-rolled rather than the DS `Table`, so they got the
            dim without the chip — the rows faded and nothing said why. Shares
            `Table`'s implementation rather than restating it.

            `rows.length > 0` is the whole condition: a COLD load has nothing to
            dim and shows its spinner row instead, and drawing both would put
            "Refreshing" on top of "Loading...".
          */}
          <RefreshOverlay
            active={isLoading && rows.length > 0}
            data-testid="catalog-table-refreshing-unmatched"
          />
          {/* Header — does not scroll. flex-shrink-0 pins it at natural height;
              overflow:hidden creates a scroll container so
              scrollbar-gutter:stable actually reserves right-side space,
              keeping column edges aligned with the body table below. */}
          <div className="tw-flex-shrink-0" style={{ overflow: 'hidden', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            {/* Column Groups for Width Definitions */}
            {/* Catalog: Checkbox | # | Product | Category | Attributes || Imported: Product | Category | Actions+Expand */}
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>

            {/* Table Header - Two Rows */}
            <thead className="tw-bg-surface">
              {/* Section Headers Row */}
              <tr className="tw-bg-surface">
                <th
                  colSpan={5}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink-secondary">
                    Product Catalog
                  </Typography>
                </th>
                <th
                  colSpan={3}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-l tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink">
                    Imported Products
                  </Typography>
                </th>
              </tr>

              {/* Column Headers Row */}
              <tr className="tw-border-b tw-border-edge tw-bg-surface">
                {/* Product Catalog Headers - grey-100 bg */}
                <th className="tw-px-2 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken">
                  <Checkbox
                    label=""
                    checked={selectAll}
                    indeterminate={selectedCount > 0 && selectedCount < rows.length}
                    onChange={handleSelectAll}
                    disabled={isEditMode}
                  />
                </th>
                <th className="tw-px-2 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken">
                  <span className="tw-text-ink-secondary">
                    <Hashtag />
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken tw-overflow-hidden">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography>
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-surface-sunken tw-overflow-hidden">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography>
                  </span>
                </th>
                <th className="tw-px-3 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-surface-sunken tw-overflow-hidden">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Attributes
                    </Typography>
                  </span>
                </th>

                {/* Imported Products Headers - grey-200 bg (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-border-l tw-border-edge tw-h-[44px] tw-bg-tone-neutral-tint tw-overflow-hidden tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('name')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography>
                    {renderSortIcon('name')}
                  </span>
                </th>
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-overflow-hidden tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('category')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography>
                    {renderSortIcon('category')}
                  </span>
                </th>
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                  {/* Actions + Expand - no header icon */}
                </th>
              </tr>
            </thead>
          </table>
          </div>

          {/* Body — independent scroll container with the SAME colgroup as the
              header. scrollbar-gutter:stable on both sides ensures columns
              stay aligned regardless of whether the vertical scrollbar
              is visible. */}
          <div className="tw-flex-1" style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>

            {/* Table Body */}
            <tbody
              /*
                Rows stay mounted through a refetch and DIM, instead of being swapped
                for a spinner row (§ Guide/ISR parity, 2026-08-31). Swapping emptied
                the table body on every page click, so the page height collapsed and
                snapped back and the row under the pointer moved — the change read as
                a reload rather than a page turn.

                `pointer-events-none` matters as much as the opacity: without it the
                stale rows stay clickable, and expanding row 3 of the OLD page while
                the new one lands is a real way to act on data that is no longer there.

                The spinner still shows on a COLD load, where there is nothing to dim.
              */
              aria-busy={isLoading || undefined}
              className={`tw-transition-opacity ${isLoading && rows.length > 0 ? 'tw-opacity-40 tw-pointer-events-none' : ''}`}
            >
              {/* Loading State */}
              {/* Cold load only — a refetch dims the rows above instead. */}
              {isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="tw-px-4 tw-py-12 tw-text-center">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[3px]">
                      <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-edge tw-border-t-accent" />
                      <Typography variant="body-primary" className="tw-text-ink-secondary">Loading...</Typography>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((row, index) => {
                const isEvenRow = index % 2 === 1;
                const cellBgClass = row.isSelected
                  ? ''
                  : `${isEvenRow ? 'tw-bg-surface-sunken' : 'tw-bg-surface'} group-hover:tw-bg-tone-neutral-tint`;

                return (
                  <React.Fragment key={row.id}>
                    {/* Main Row */}
                    <tr
                      className={`tw-group tw-border-b tw-border-edge tw-h-12 ${row.isSelected ? 'tw-bg-accent-tint' : 'hover:tw-bg-tone-neutral-tint'}`}
                    >
                      {/* Product Catalog Columns - Show "Product Not Found" indicator */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <Checkbox
                          label=""
                          checked={row.isSelected || false}
                          onChange={(checked) => handleRowSelect(index, checked)}
                          disabled={isEditMode}
                        />
                      </td>
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <Typography variant="title-header" className="tw-text-ink">
                          {(pagination.page - 1) * pagination.perPage + index + 1}
                        </Typography>
                      </td>
                      {/* Product Catalog columns — once the user has picked a catalog product
                          via Search & Add (row.matchedGmsId set, existing_product populated),
                          render the three catalog cells just like SKU Matched tab. Until then,
                          fall back to the "Product Not Found" CTA that opens Search & Add. */}
                      {row.matchedGmsId ? (
                        <>
                          <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`} title={row.catalog.product}>
                            <Typography variant="title" className="tw-text-ink-secondary tw-truncate">
                              {row.catalog.product}
                            </Typography>
                          </td>
                          <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                            <div className="tw-truncate" title={row.catalog.category}>
                              <Typography variant="title" className="tw-text-ink-secondary tw-truncate">
                            {(row.catalog.category || '')}
                          </Typography>
                            </div>
                          </td>
                          <td className={`tw-px-3 tw-py-2 tw-text-center ${cellBgClass}`}>
                            <Typography variant="title" className="tw-text-ink-secondary">
                              {row.catalog.attributeCount}
                            </Typography>
                          </td>
                        </>
                      ) : (
                        <td colSpan={3} className={`tw-px-3 tw-py-2 ${cellBgClass}`}>
                          <ProductNotFoundIndicator onSearchProduct={() => handleSearchProduct(index)} />
                        </td>
                      )}

                      {/* Imported Products Columns */}
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden tw-border-l tw-border-edge ${cellBgClass}`} title={row.imported.product}>
                        {isEditMode ? (
                          <input data-testid={`unmatchedview-unmatchedview-input-1-${index}`}
                            type="text"
                            value={row.imported.product || ''}
                            onChange={(e) => handleProductNameChange(index, e.target.value)}
                            className="tw-text-sm tw-font-semibold tw-text-ink tw-px-2 tw-py-1 tw-rounded tw-bg-surface tw-w-full tw-border tw-border-tone-warning-glyph"
                          />
                        ) : (
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {row.imported.product}
                          </Typography>
                        )}
                      </td>
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={row.imported.category}>
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {(row.imported.category || '')}
                          </Typography>
                        </div>
                      </td>
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-[20px]">
                          <RowActionsMenu
                            actions={UNMATCHED_ACTIONS.map((a) => (
                              // Update requires a catalog product picked via Search & Add;
                              // without it, action="update" is a backend no-op.
                              a.type === 'update' && !row.matchedGmsId
                                ? { ...a, disabled: true }
                                : a
                            ))}
                            onAction={(actionType) => handleRowAction(index, actionType)}
                            disabled={!hasUpdatePermission || isEditMode}
                          />
                          <button data-testid={`unmatchedview-unmatchedview-button-2-${index}`}
                            onClick={() => handleRowExpand(index)}
                            className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
                          >
                            {row.isExpanded ? <ArrowDown /> : <ArrowRight />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row Content — reuses SKU Matched layout via
                        ExpandedRowContent so the catalog/imported attributes
                        align row-for-row (new attrs on the right leave the
                        left blank, removed attrs vice versa), diff colors
                        render, and the properties footer is shown. */}
                    {row.isExpanded && (row.attributes || row.catalogAttributes) && (
                      <ExpandedRowContent
                        // Must sum to 100% to match the outer table's colgroup
                        // (also 100%) — otherwise table-fixed scales the inner
                        // widths by 100/sum and the catalog↔imported divider
                        // (border-l at col 5) shifts right of the outer divider.
                        // Catalog 3+3+21+17+11=55%, imported 15+9+5+5+5+4+2=45%.
                        colWidths={['3%', '3%', '21%', '17%', '11%', '15%', '9%', '5%', '5%', '5%', '4%', '2%']}
                        catalogAttributes={row.catalogAttributes || []}
                        importedAttributes={row.attributes || []}
                        catalogMetadata={{
                          description: row.catalog.description ?? '',
                          sku: row.catalog.sku ?? '',
                          price: row.catalog.price ?? '',
                          upc: row.catalog.upc ?? '',
                          modelNumber: row.catalog.modelNumber ?? '',
                          pls: row.catalog.pls ?? '',
                          parentId: row.catalog.parentId ?? '',
                          version: row.catalog.version ?? '',
                          realSku: row.catalog.realSku ?? '',
                        }}
                        importedMetadata={{
                          description: row.imported.description ?? '',
                          sku: row.imported.sku ?? '',
                          price: row.imported.price ?? '',
                          upc: row.imported.upc ?? '',
                          modelNumber: row.imported.modelNumber ?? '',
                          pls: row.imported.pls ?? '',
                          parentId: row.imported.parentId ?? '',
                          version: row.imported.version ?? '',
                          realSku: row.imported.realSku ?? '',
                          updatedFields: row.imported.updatedFields,
                        }}
                        isEditMode={isEditMode}
                        onDeleteAttribute={(attrIndex) => handleDeleteAttribute(index, attrIndex)}
                        onDeleteAttributeValue={(attrIndex, valueIndex) =>
                          handleDeleteAttributeValue(index, attrIndex, valueIndex)
                        }
                        onRevertAttribute={(attrIndex) => handleRevertAttribute(index, attrIndex)}
                        onRevertAttributeValue={(attrIndex, valueIndex) =>
                          handleRevertAttributeValue(index, attrIndex, valueIndex)
                        }
                        onModalAttrValuesSave={(attrName, valuesToKeep) =>
                          handleModalAttrValuesSave(index, attrName, valuesToKeep)
                        }
                        onEditModeEnter={() => {
                          if (!isEditMode) {
                            setIsEditMode(true);
                            onEditModeChange?.(true);
                            onRegisterEditHandlers?.({ save: () => saveRef.current(), cancel: () => cancelRef.current() });
                          }
                        }}
                        onMetadataChange={(field, value) => handleMetadataChange(index, field, value)}
                        onAddAttributeValue={(attrIndex, value) =>
                          handleAddAttributeValue(index, attrIndex, value)
                        }
                        onAddNewAttribute={() => handleAddNewAttribute(index)}
                        onSelectNewAttributeName={(attrIndex, name) =>
                          handleSelectNewAttributeName(index, attrIndex, name)
                        }
                        loadValueOptions={loadValueOptionsForRow(index)}
                        loadAttributeOptions={() => loadAttributeOptionsForRow(index)}
                        onCreateNewValue={(attributeName) =>
                          setCreateValueModal({ rowIndex: index, attributeName })
                        }
                        onCreateNewAttribute={(attrIndex) =>
                          setCreateAttributeModal({ rowIndex: index, attrIndex })
                        }
                        productName={row.imported.product || row.catalog.product}
                      />
                    )}

                    {/* Action-button footer — mirrors MPN/UPC Matched layout.
                        Only shown when the expanded row already has a matched
                        catalog product (Search & Add was used). Lets the user
                        pick a different catalog product via the same side panel. */}
                    {row.isExpanded && row.matchedGmsId && (
                      <tr className="tw-border-b tw-border-edge tw-bg-surface">
                        <td colSpan={5} className="tw-px-4 tw-py-3 tw-bg-surface">
                          <div className="tw-flex tw-justify-end">
                            <Button
                              variant="primary"
                              icon={<SearchIcon />}
                              onClick={() => handleSearchProduct(index)}
                              disabled={!hasUpdatePermission || isEditMode}
                            >
                              Search &amp; Replace Product
                            </Button>
                          </div>
                        </td>
                        <td colSpan={3} className="tw-px-4 tw-py-3 tw-bg-surface tw-border-l tw-border-edge" />
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {!isLoading && rows.length === 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                No unmatched products found
              </Typography>
            </div>
          )}
          </div>

          {/*
             The guard no longer includes `!isLoading`. It used to, and the strip therefore
             UNMOUNTED on every page click: the button vanished from under the pointer,
             keyboard focus fell to <body>, and the row it occupied collapsed so the page
             jumped. It stays mounted and takes `disabled` instead — which is what Guide/ISR's
             equivalent does.
          
             What remains here is `total > perPage`, and it stays at the CALL SITE: whether a
             table needs a pager at all is the view's question, not the bar's.
          */}
          {pagination.total > pagination.perPage && (
            <TablePaginationBar
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid="catalog-pagination-unmatched"
            />
          )}
        </div>
      )}

      {/* SearchReplaceSidePanel */}
      {sidePanelRowIndex !== null && rows[sidePanelRowIndex] && (
        <SearchReplaceSidePanel
          isOpen={true}
          onClose={handleSidePanelClose}
          channelKey={channelKey}
          sku={rows[sidePanelRowIndex].imported.sku || ''}
          importedProduct={{
            name: rows[sidePanelRowIndex].imported.product,
            category: rows[sidePanelRowIndex].imported.category,
            sku: rows[sidePanelRowIndex].imported.sku,
            price: rows[sidePanelRowIndex].imported.price,
            upc: rows[sidePanelRowIndex].imported.upc,
            modelNumber: rows[sidePanelRowIndex].imported.modelNumber,
            description: rows[sidePanelRowIndex].imported.description,
            pls: rows[sidePanelRowIndex].imported.pls,
            parentId: rows[sidePanelRowIndex].imported.parentId,
            version: rows[sidePanelRowIndex].imported.version,
            realSku: rows[sidePanelRowIndex].imported.realSku,
            attributes: rows[sidePanelRowIndex].attributes || [],
          }}
          onReplaceProduct={handleReplaceProduct}
          isReplacing={isReplacingProduct}
          mode="add"
        />
      )}

      {/* Page-level LoadingOverlay only fires for view-wide ops. The
          per-row Search & Add flow renders its loading state inside
          the side panel (see `isReplacing` above). */}
      {(isBulkActionLoading || isSaveLoading) && (
        <LoadingOverlay message={isEditMode ? 'Saving changes...' : 'Processing...'} />
      )}

      {/* Ignore Product Modal */}
      <IgnoreProductModal
        isOpen={showIgnoreModal}
        onClose={() => { setShowIgnoreModal(false); setPendingIgnoreSkus([]); clearSelection(); }}
        onSubmit={handleIgnoreProductSubmit}
        // Single-row Ignore shows the product name; bulk Ignore shows
        // an N-products summary instead so the user sees the scope.
        productName={
          pendingIgnoreSkus.length === 1
            ? rows.find((r) => r.imported.sku === pendingIgnoreSkus[0])?.imported.product
            : pendingIgnoreSkus.length > 1
              ? `${pendingIgnoreSkus.length} products selected`
              : undefined
        }
      />

      {/* Attribute Values "Show All" modal is owned by ExpandedRowContent now. */}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        cancelLabel="Cancel"
      />

      {/* 11443: consolidated delete-attribute confirmation. Triggered from
          handleSaveChanges when one or more pending-new attributes are still
          incomplete (no name, or no values). AC wording is verbatim. */}
      <ConfirmActionModal
        isOpen={deleteAttrConfirm.isOpen}
        onClose={closeDeleteAttrConfirm}
        onConfirm={deleteAttrConfirm.onConfirm}
        title="Delete attribute?"
        message={`You have not selected any values for: ${deleteAttrConfirm.names.join(', ')}. Do you want to delete ${deleteAttrConfirm.names.length === 1 ? 'this attribute' : 'these attributes'}?`}
        confirmLabel="Yes, delete"
        cancelLabel="No, go back"
      />

      {createValueModal && (
        <CreateAttributeValueModal
          isOpen
          onClose={() => setCreateValueModal(null)}
          onAdd={(values) => {
            const row = rows[createValueModal.rowIndex];
            const attrIdx = (row?.attributes || []).findIndex(
              (a) => a.name === createValueModal.attributeName,
            );
            if (attrIdx >= 0) {
              values.forEach((v) =>
                handleAddAttributeValue(createValueModal.rowIndex, attrIdx, v),
              );
            }
            setCreateValueModal(null);
          }}
          productName={rows[createValueModal.rowIndex]?.imported.product || ''}
          category={rows[createValueModal.rowIndex]?.imported.category || ''}
          attributeName={createValueModal.attributeName}
          csKey={channelKey}
          showInfoBar={false}
        />
      )}

      {createAttributeModal && (
        <CreateAttributeModal
          isOpen
          onClose={() => setCreateAttributeModal(null)}
          onCreate={(name) => {
            handleSelectNewAttributeName(
              createAttributeModal.rowIndex,
              createAttributeModal.attrIndex,
              name,
            );
            setCreateAttributeModal(null);
          }}
          productName={rows[createAttributeModal.rowIndex]?.imported.product || ''}
          category={rows[createAttributeModal.rowIndex]?.imported.category || ''}
          categoryUid={rows[createAttributeModal.rowIndex]?.imported.categoryId || ''}
          csKey={channelKey}
          existingAttributeNames={(rows[createAttributeModal.rowIndex]?.attributes || [])
            .map((a) => a.name)
            .filter((n) => n && !n.startsWith('__pending_'))}
          showInfoBar={false}
        />
      )}
    </div>
  );
};

export default UnmatchedView;
